"use client";
import { useEffect, useState } from "react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import clsx from "clsx";
import Autoplay from "embla-carousel-autoplay";
import LazyImage from "@/components/LazyImage";

interface UnitTypeSpec {
  id: string;
  short_label: string;
  unit: string;
  area: string;
  images: string[];
  facility: { id: string; label: string; icon: string }[];
  map: string;
  mini_map: string;
}
export interface UnitTypeProps {
  content: {
    title: string;
    id: string;
    specification: UnitTypeSpec[];
  }[];
}

export default function UnitType(props: UnitTypeProps) {
  const [activeType, setActiveType] = useState(props.content[0]);

  const [activeSpecUnitType, setActiveSpecUnitType] = useState(
    props.content[0].specification[0]
  );

  const onChangeSpecUnitType = (unitType: UnitTypeSpec) => {
    setActiveSpecUnitType(unitType);
  };

  const onChangeUnitType = (id: string) => {
    const defaultUnitType =
      props.content.find((content) => content.id === id) || props.content[0];
    setActiveType(defaultUnitType);
    setActiveSpecUnitType(defaultUnitType.specification[0]);
  };

  useEffect(() => {
    setActiveType(props.content[0]);
    setActiveSpecUnitType(props.content[0].specification[0]);
  }, [props.content]);

  return (
    <div className="bg-coffebrown py-10 xl:pt-24 xl:pb-28">
      <div className="w-full xl:max-w-screen-xl mx-auto px-9 xl:px-0">
        <h4 className="text-white xl:text-5xl text-center font-pluto">
          Unit Type
        </h4>

        <Tabs
          value={activeType.id}
          className="mt-6 xl:mt-14 gap-0 xl:gap-2"
          onValueChange={onChangeUnitType}
        >
          <TabsList className="bg-transparent flex w-full">
            {props.content.map((content) => {
              return (
                <TabsTrigger
                  key={content.id}
                  className="text-white flex-1 pb-4 text-[10px] xl:text-xl font-pluto cursor-pointer uppercase"
                  value={content.id}
                >
                  {content.title}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {props.content.map((content) => {
            const [nett, semi_gross] = activeSpecUnitType.area.split("/");
            return (
              <TabsContent key={content.id} value={content.id}>
                <div className="mt-2 xl:mt-6">
                  <Carousel
                    opts={{ loop: true }}
                    plugins={[
                      Autoplay({
                        delay: 5_000,
                      }),
                    ]}
                    className="w-full relative"
                  >
                    <CarouselContent>
                      {activeSpecUnitType.images.map((image, idx) => {
                        return (
                          <CarouselItem key={idx}>
                            <div className="relative">
                              <LazyImage
                                src={image}
                                className="rounded-[9px] xl:rounded-[20px] h-[261px] xl:h-[530px] w-full object-cover"
                                rootMargin="50px 0px 0px 0px"
                              />
                              <div className="absolute bottom-0 w-full rounded-[9px] xl:rounded-[20px] h-[35%] bg-gradient-to-t from-brown to-transparent"></div>
                            </div>
                          </CarouselItem>
                        );
                      })}
                    </CarouselContent>
                    <CarouselPrevious className=" size-5 xl:size-10 left-4 xl:left-10 bg-transparent cursor-pointer text-white z-10 border-2 !border-white" />
                    <CarouselNext className=" size-5 xl:size-10 right-4 xl:right-10 bg-transparent cursor-pointer text-white z-10 border-2 border-white" />

                    <div className="absolute bottom-0 left-0 w-full px-3 xl:px-10 pb-2 xl:pb-6 flex items-center z-0">
                      <div>
                        <p
                          className="font-medium text-[7px] xl:text-2xl text-white"
                          dangerouslySetInnerHTML={{
                            __html: activeSpecUnitType.unit,
                          }}
                        ></p>
                        <p className="font-medium text-white text-[12px] xl:text-5xl">
                          {semi_gross} sqm
                        </p>
                      </div>

                      <div className="ml-auto flex space-x-1 xl:space-x-5">
                        {activeSpecUnitType.facility.map((facility) => {
                          return (
                            <div
                              key={facility.id}
                              className="flex flex-col items-center"
                            >
                              <LazyImage
                                src={facility.icon}
                                loading="lazy"
                                alt="bedroom"
                                width={40}
                                height={40}
                                className="w-4 xl:w-10 h-4 xl:h-10"
                                rootMargin="50px 0px 0px 0px"
                              />
                              <p className="text-white text-[6px] xl:text-xl font-medium">
                                {facility.label}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Carousel>

                  <div className="mt-3 xl:mt-6 w-full text-center flex justify-center  flex-wrap gap-x-2.5 xl:gap-x-5">
                    {content.specification.map((spec) => (
                      <button
                        key={spec.id}
                        className={clsx(
                          "px-5 xl:px-10 py-1 xl:py-1.5 text-[10px] xl:text-3xl rounded-[53px] border border-gold cursor-pointer",
                          {
                            "bg-gold text-white":
                              activeSpecUnitType.id === spec.id,
                          }
                        )}
                        onClick={() => onChangeSpecUnitType(spec)}
                      >
                        <span className="font-medium font-pluto text-white">
                          {spec.short_label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </div>
  );
}
